import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatMoney, formatDate } from "../lib/format";
import { useAlert } from "../components/Confirm";

/**
 * Annual Funder Uplifts tracker.
 *
 * No submit/approve flow — Commercial Finance (James) is the actor. One row
 * per funder per FY. Status flow: Pending -> Proposed -> Accepted (or Rejected
 * / Withdrawn). Editing is inline; saves stamp proposed_at / accepted_at
 * automatically when the status transitions.
 */
const STATUS_OPTIONS = ["pending", "proposed", "accepted", "rejected", "withdrawn"] as const;
type Status = typeof STATUS_OPTIONS[number];

interface UpliftRow {
  funder_id: number;
  fiscal_year: string;
  funder_name: string;
  funder_region: string | null;
  partnership_rag: string | null;
  uplift_pct: number | null;
  status: Status;
  baseline_weekly_fee: number | null;
  proposed_weekly_fee: number | null;
  proposed_at: string | null;
  accepted_at: string | null;
  notes: string | null;
  case_by_case: boolean;
  case_by_case_deadline: string | null;
}

export function Uplifts() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["uplifts"], queryFn: api.upliftsList });
  const alertModal = useAlert();

  const [filter, setFilter] = useState<"all" | Status>("all");
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState<Record<number, Partial<UpliftRow>>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const all: UpliftRow[] = list.data?.uplifts ?? [];
  const counts: Record<string, number> = list.data?.counts ?? {};
  const fy: string = list.data?.fiscal_year ?? "FY27";

  const filtered = useMemo(() => {
    return all.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!r.funder_name.toLowerCase().includes(q) && !(r.funder_region ?? "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [all, filter, search]);

  function patchDraft(id: number, patch: Partial<UpliftRow>) {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  }
  function rowValue<K extends keyof UpliftRow>(row: UpliftRow, key: K): UpliftRow[K] {
    const d = drafts[row.funder_id];
    return (d && key in d ? (d[key] as UpliftRow[K]) : row[key]);
  }
  function isDirty(row: UpliftRow): boolean {
    const d = drafts[row.funder_id];
    if (!d) return false;
    return Object.keys(d).some((k) => (d as any)[k] !== (row as any)[k]);
  }

  async function save(row: UpliftRow) {
    setSavingId(row.funder_id);
    try {
      await api.upliftUpdate(row.funder_id, {
        status:              rowValue(row, "status"),
        uplift_pct:          rowValue(row, "uplift_pct"),
        baseline_weekly_fee: rowValue(row, "baseline_weekly_fee"),
        proposed_weekly_fee: rowValue(row, "proposed_weekly_fee"),
        notes:               rowValue(row, "notes"),
      });
      setDrafts((d) => { const c = { ...d }; delete c[row.funder_id]; return c; });
      await qc.invalidateQueries({ queryKey: ["uplifts"] });
    } catch (err) {
      await alertModal({ title: "Couldn't save", body: String(err) });
    } finally {
      setSavingId(null);
    }
  }

  const acceptedAnnualTotal = useMemo(() => {
    return all
      .filter((r) => r.status === "accepted" && r.proposed_weekly_fee != null && r.baseline_weekly_fee != null)
      .reduce((s, r) => s + (Number(r.proposed_weekly_fee) - Number(r.baseline_weekly_fee)) * 52, 0);
  }, [all]);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/" className="text-xs text-slate-500 hover:text-slate-700">← Back</Link>
        <div className="flex items-baseline justify-between mt-2 flex-wrap gap-3">
          <h1 className="text-2xl font-semibold text-liaise-navy">Annual funder uplifts <span className="text-slate-400 text-base font-normal">{fy}</span></h1>
          <div className="text-sm text-slate-600">
            Accepted annual uplift uplift: <b>{formatMoney(acceptedAnnualTotal)}</b>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <FilterChip current={filter} value="all"        label={`All (${all.length})`}                       onChange={setFilter} />
            <FilterChip current={filter} value="pending"    label={`Pending (${counts.pending     ?? 0})`}      onChange={setFilter} />
            <FilterChip current={filter} value="proposed"   label={`Proposed (${counts.proposed   ?? 0})`}      onChange={setFilter} />
            <FilterChip current={filter} value="accepted"   label={`Accepted (${counts.accepted   ?? 0})`}      onChange={setFilter} />
            <FilterChip current={filter} value="rejected"   label={`Rejected (${counts.rejected   ?? 0})`}      onChange={setFilter} />
            <FilterChip current={filter} value="withdrawn"  label={`Withdrawn (${counts.withdrawn ?? 0})`}      onChange={setFilter} />
          </div>
          <input
            placeholder="Search funder / region..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg w-64 focus:outline-none focus:border-liaise-blue"
          />
        </div>

        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left  px-4 py-2.5 font-medium">Funder</th>
              <th className="text-right px-3 py-2.5 font-medium w-28">Baseline £/wk</th>
              <th className="text-right px-3 py-2.5 font-medium w-28">Proposed £/wk</th>
              <th className="text-right px-3 py-2.5 font-medium w-20">Uplift %</th>
              <th className="text-left  px-3 py-2.5 font-medium w-32">Status</th>
              <th className="text-left  px-3 py-2.5 font-medium">Notes</th>
              <th className="text-right px-3 py-2.5 font-medium w-24">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => {
              const dirty = isDirty(row);
              const status = rowValue(row, "status");
              return (
                <tr key={row.funder_id} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-liaise-navy">{row.funder_name}</div>
                    <div className="text-xs text-slate-500">
                      {row.funder_region ?? "no region"}
                      {row.case_by_case && <span className="ml-2 text-orange-700">⚠ Case-by-case</span>}
                    </div>
                    {row.accepted_at && status === "accepted" && (
                      <div className="text-[10px] text-emerald-700 mt-1">Accepted {formatDate(row.accepted_at)}</div>
                    )}
                    {row.proposed_at && status === "proposed" && (
                      <div className="text-[10px] text-orange-700 mt-1">Proposed {formatDate(row.proposed_at)}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <input type="number" step={0.01} className="num"
                      value={rowValue(row, "baseline_weekly_fee") ?? ""}
                      onChange={(e) => patchDraft(row.funder_id, { baseline_weekly_fee: e.target.value === "" ? null : Number(e.target.value) })} />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <input type="number" step={0.01} className="num"
                      value={rowValue(row, "proposed_weekly_fee") ?? ""}
                      onChange={(e) => patchDraft(row.funder_id, { proposed_weekly_fee: e.target.value === "" ? null : Number(e.target.value) })} />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <input type="number" step={0.1} className="num"
                      value={(rowValue(row, "uplift_pct") ?? "") === "" ? "" : (Number(rowValue(row, "uplift_pct")) * 100).toFixed(1)}
                      onChange={(e) => patchDraft(row.funder_id, { uplift_pct: e.target.value === "" ? null : Number(e.target.value) / 100 })} />
                  </td>
                  <td className="px-3 py-3">
                    <select className="num"
                      value={status}
                      onChange={(e) => patchDraft(row.funder_id, { status: e.target.value as Status })}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <input type="text" className="w-full px-2 py-1 text-xs border border-slate-200 rounded"
                      placeholder="Notes"
                      value={rowValue(row, "notes") ?? ""}
                      onChange={(e) => patchDraft(row.funder_id, { notes: e.target.value })} />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button
                      disabled={!dirty || savingId === row.funder_id}
                      onClick={() => save(row)}
                      className={`text-xs px-3 py-1.5 rounded ${
                        dirty
                          ? "bg-liaise-blue text-white hover:bg-blue-700"
                          : "bg-slate-100 text-slate-400 cursor-not-allowed"
                      }`}
                    >
                      {savingId === row.funder_id ? "..." : "Save"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={7} className="text-center text-slate-400 px-4 py-10 text-sm">No matching uplifts.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`.num{padding:.35rem .5rem;border:1px solid rgb(226 232 240);border-radius:.375rem;font-size:.8125rem;text-align:right;width:100%;background:white}`}</style>
    </div>
  );
}

function FilterChip({ current, value, label, onChange }: { current: string; value: any; label: string; onChange: (v: any) => void }) {
  return (
    <button
      onClick={() => onChange(value)}
      className={`text-xs px-3 py-1 rounded-full ${
        current === value ? "bg-liaise-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
      }`}
    >
      {label}
    </button>
  );
}
